import { t } from "@lingui/macro";
import type { ResumeDto } from "@reactive-resume/dto";
import { CaretDownIcon, FilePlusIcon, NotePencilIcon, PlusIcon } from "@phosphor-icons/react";
import { cn, resolveTemplate } from "@reactive-resume/utils";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { templateCardUrl } from "@/client/constants/template-images";
import { useDialog } from "@/client/stores/dialog";
import { useResumeDownload } from "@/client/services/resume/download-cta";

import { useFormResumeCreate } from "../../dashboard/_components/form-create";
import { USER_SPACE_PATHS } from "../config/navigation";
import { ResumeDownloadDialog } from "../documents/components/resume-download-dialog";
import {
  RecentDocumentCard,
  type RecentDocumentAction,
  type RecentDocumentItem,
} from "./recent-document-card";

type RecentDocumentsProps = {
  resumes: ResumeDto[];
};

const MAX_VISIBLE_DOCUMENTS = 6;

const convertResumeToDocument = (resume: ResumeDto): RecentDocumentItem => {
  const template = resolveTemplate(resume.data.metadata.template);

  return {
    id: resume.id,
    kind: "resume",
    title: resume.title,
    subtitle: "CV",
    template,
    thumbnailUrl: templateCardUrl(template),
    updatedAt: String(resume.updatedAt),
  };
};

export const RecentDocuments = ({ resumes }: RecentDocumentsProps) => {
  const navigate = useNavigate();

  const creationForm = useFormResumeCreate();

  const resumeDialog = useDialog<ResumeDto>("resume");

  const [documentToDownload, setDocumentToDownload] = useState<RecentDocumentItem | null>(null);

  const { handleDownload, loading: downloadLoading } = useResumeDownload();

  const resumeById = useMemo(
    () => new Map(resumes.map((resume) => [resume.id, resume])),
    [resumes],
  );

  const resumeDocuments = useMemo(() => resumes.map(convertResumeToDocument), [resumes]);

  const visibleDocuments = useMemo(
    () =>
      [...resumeDocuments]
        .sort(
          (first, second) =>
            new Date(second.updatedAt).getTime() - new Date(first.updatedAt).getTime(),
        )
        .slice(0, MAX_VISIBLE_DOCUMENTS),
    [resumeDocuments],
  );

  const openResumeCreation = () => {
    creationForm?.setformCreateResume(true);
  };

  const openDocument = (document: RecentDocumentItem) => {
    if (document.kind !== "resume") {
      return;
    }

    void navigate(`/client/builder/${document.id}`);
  };

  const handleDocumentAction = (document: RecentDocumentItem, action: RecentDocumentAction) => {
    if (document.kind !== "resume") {
      return;
    }

    const resume = resumeById.get(document.id);

    if (!resume) {
      return;
    }

    switch (action) {
      case "edit": {
        void navigate(`/client/builder/${resume.id}`);
        break;
      }

      case "duplicate": {
        resumeDialog.open("duplicate", {
          id: "resume",
          item: resume,
        });
        break;
      }

      case "delete": {
        resumeDialog.open("delete", {
          id: "resume",
          item: resume,
        });
        break;
      }

      case "download": {
        setDocumentToDownload(document);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <>
      <div
        className={cn(
          "relative z-20 flex flex-col gap-4",
          "lg:flex-row lg:items-center lg:justify-between",
        )}
      >
        <div className={cn("flex min-w-0 flex-col gap-3", "sm:flex-row sm:items-center")}>
          <h2
            id="resumes-title"
            className="shrink-0 text-base font-bold text-indigo-950 sm:text-lg"
          >
            {t`Mes CV`}
          </h2>
        </div>

        <details className="group relative z-[70] w-full sm:w-auto">
          <summary
            className={cn(
              "flex min-h-11 w-full cursor-pointer list-none",
              "items-center justify-center gap-2 rounded-xl",
              "bg-gradient-to-r from-indigo-700 via-violet-700 to-indigo-600",
              "px-4 text-xs font-semibold text-white",
              "shadow-[0_8px_18px_rgba(79,70,229,0.2)]",
              "transition-all duration-200",
              "hover:-translate-y-0.5",
              "hover:shadow-[0_12px_24px_rgba(79,70,229,0.26)]",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
              "sm:w-auto",
              "[&::-webkit-details-marker]:hidden",
            )}
          >
            <PlusIcon aria-hidden size={17} weight="bold" />
            Créer nouveau
            <CaretDownIcon
              aria-hidden
              size={15}
              weight="bold"
              className="transition-transform group-open:rotate-180"
            />
          </summary>

          <div
            className={cn(
              "absolute right-0 top-[52px] z-[80] w-full min-w-56",
              "overflow-hidden rounded-xl border border-slate-200",
              "bg-white p-1.5",
              "shadow-[0_18px_42px_rgba(15,23,42,0.16)]",
              "sm:w-60",
            )}
          >
            <button
              type="button"
              onClick={openResumeCreation}
              className={cn(
                "flex min-h-12 w-full items-center gap-3 rounded-lg",
                "px-3 text-left text-xs font-semibold text-indigo-950",
                "transition-colors hover:bg-indigo-50 hover:text-indigo-700",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
              )}
            >
              <FilePlusIcon aria-hidden size={19} weight="duotone" className="text-indigo-600" />
              Créer un CV
            </button>

            <div className="mx-2 h-px bg-slate-100" />

            <Link
              to={USER_SPACE_PATHS.coverLetters}
              className={cn(
                "flex min-h-12 w-full items-center gap-3 rounded-lg",
                "px-3 text-left text-xs font-semibold text-indigo-950",
                "transition-colors hover:bg-indigo-50 hover:text-indigo-700",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
              )}
            >
              <NotePencilIcon aria-hidden size={19} weight="duotone" className="text-indigo-600" />
              Lettre de motivation
            </Link>
          </div>
        </details>
      </div>

      <p className="mt-4 text-xs font-medium text-slate-600">Modifiés récemment</p>

      {visibleDocuments.length > 0 ? (
        <div className={cn("relative z-10 mt-3 grid gap-3", "md:grid-cols-2", "xl:grid-cols-3")}>
          {visibleDocuments.map((document) => (
            <RecentDocumentCard
              key={document.id}
              document={document}
              onOpen={openDocument}
              onAction={handleDocumentAction}
              isDownloading={downloadLoading && documentToDownload?.id === document.id}
            />
          ))}
        </div>
      ) : (
        <div
          className={cn(
            "mt-3 flex min-h-40 flex-col items-center justify-center",
            "rounded-2xl border border-dashed border-slate-300",
            "bg-slate-50 px-4 text-center",
          )}
        >
          <p className="text-sm font-bold text-indigo-950">{t`Aucun CV pour le moment`}</p>

          <p className="mt-1 max-w-md text-xs leading-5 text-slate-500">{t`Créez ou importez votre premier CV avec les cartes ci-dessus.`}</p>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Link
          to={USER_SPACE_PATHS.resumes}
          className={cn(
            "rounded-lg text-xs font-semibold text-indigo-600",
            "transition-colors hover:text-indigo-800",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          )}
        >
          Voir tout
        </Link>
      </div>

      <ResumeDownloadDialog
        resume={documentToDownload ? resumeById.get(documentToDownload.id) : undefined}
        open={!!documentToDownload}
        loading={downloadLoading}
        onOpenChange={(open) => !open && setDocumentToDownload(null)}
        onConfirm={() => {
          if (documentToDownload) {
            void handleDownload(documentToDownload.id).then(() => setDocumentToDownload(null));
          }
        }}
      />
    </>
  );
};
